import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TrendingUp, X } from 'lucide-react';
import { DeltaConsumo } from './consumoHistorico';

interface DeltaPopoverProps {
  /** Retângulo do ícone-âncora (getBoundingClientRect) para posicionar o popover. */
  anchor: DOMRect;
  onClose: () => void;
  delta: DeltaConsumo | null;
  consumoMedioKwh: number;
  /** false quando a UC está travada (Percentual Fixo) — Aplicar Sugestão vira no-op. */
  podeAplicar: boolean;
  onAplicarSugestao: () => void;
}

const POPOVER_W = 280;

const formatKwh = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Popover da Revisão Inteligente (Delta) — Seção 4/10 da spec. Compara o
 * consumo real da fatura mais recente da UC com o consumo_medio_kwh cadastrado.
 */
const DeltaPopover: React.FC<DeltaPopoverProps> = ({ anchor, onClose, delta, consumoMedioKwh, podeAplicar, onAplicarSugestao }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: anchor.bottom + 6, left: anchor.left });

  useLayoutEffect(() => {
    const h = ref.current?.offsetHeight || 180;
    const margin = 8;
    let top = anchor.bottom + 6;
    if (top + h > window.innerHeight - margin) top = Math.max(margin, anchor.top - h - 6);
    let left = anchor.left;
    if (left + POPOVER_W > window.innerWidth - margin) left = window.innerWidth - POPOVER_W - margin;
    if (left < margin) left = margin;
    setPos({ top, left });
  }, [anchor]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const podeAplicarSugestao = !!delta && podeAplicar;

  return createPortal(
    <div className="fixed inset-0 z-[2100]" onClick={onClose}>
      <div
        ref={ref}
        style={{ top: pos.top, left: pos.left, width: POPOVER_W }}
        className="fixed bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 p-4 animate-fade-in"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-bold text-gray-800 dark:text-white flex items-center gap-1.5">
            <TrendingUp size={14} className="text-amber-500" /> Revisão Inteligente (Delta)
          </h4>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X size={16} />
          </button>
        </div>

        <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          {delta ? (
            <>
              Último mês ({delta.mesReferenciaUltimoMes}): <strong className="text-gray-700 dark:text-gray-200">{formatKwh(delta.consumoUltimoMesKwh)} kWh</strong>
              {' '}vs. média cadastrada <strong className="text-gray-700 dark:text-gray-200">{formatKwh(consumoMedioKwh)} kWh</strong>
              {delta.deltaPercent != null && (
                <>
                  {' '}— variação de{' '}
                  <strong className={delta.alerta ? 'text-amber-600 dark:text-amber-400' : 'text-gray-700 dark:text-gray-200'}>
                    {delta.deltaPercent > 0 ? '+' : ''}
                    {delta.deltaPercent.toFixed(2)}%
                  </strong>
                </>
              )}
              {!podeAplicar && <span className="block mt-1 text-gray-400">UC travada — destrave para aplicar a sugestão.</span>}
            </>
          ) : (
            'Nenhuma fatura recente encontrada para esta UC — sem base de comparação ainda.'
          )}
        </div>

        <div className="flex gap-2">
          <button
            disabled={!podeAplicarSugestao}
            onClick={() => {
              onAplicarSugestao();
              onClose();
            }}
            className="flex-1 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-medium disabled:bg-primary/50 disabled:cursor-not-allowed"
          >
            Aplicar Sugestão
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Ignorar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DeltaPopover;
